import React, { useMemo } from 'react';
import * as echarts from 'echarts';
import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import titleIcon from '@/assets/images/title_icon.png';
import { formatPercent } from '../../utils';
import type { AnalysisGroup, RealtimeTrendPoint } from '../../types';
import styles from './index.module.scss';

interface RealtimeLoadSectionProps {
  mediumName: string;
  groups: AnalysisGroup[];
  trend: RealtimeTrendPoint[];
}

const getHighDuration = (trend: RealtimeTrendPoint[]) => {
  const highCount = trend.filter(item => item.loadRate >= 80).length;
  return highCount ? `${highCount * 60}分钟` : '0分钟';
};

const RealtimeLoadSection: React.FC<RealtimeLoadSectionProps> = ({ mediumName, groups, trend }) => {
  const theme = useTheme();
  const plantRow = groups.find(group => group.key === 'plant')?.rows[0];
  const current = trend[trend.length - 1]?.loadRate ?? plantRow?.realtimeLoadRate;
  const max = trend.length ? Math.max(...trend.map(item => item.loadRate)) : plantRow?.maxLoadRate;
  const avg = trend.length
    ? trend.reduce((total, item) => total + Number(item.loadRate), 0) / trend.length
    : plantRow?.avgLoadRate;

  const option = useMemo(
    () => ({
      backgroundColor: 'transparent',
      color: ['#16DD8E', '#FFCB47'],
      tooltip: {
        trigger: 'axis',
        formatter: (params: Array<{ marker: string; seriesName: string; value: number }>) =>
          `<div style="width:220px;">
            ${params
              .map(
                item =>
                  `<div style="display:flex;align-items:center;justify-content:space-between;line-height:24px;">
                    <span>${item.marker}${item.seriesName}</span>
                    <span style="font-weight:600;color:#fff;">${formatPercent(item.value)}</span>
                  </div>`
              )
              .join('')}
          </div>`,
      },
      legend: {
        right: 20,
        top: 4,
        itemWidth: 16,
        itemHeight: 8,
        textStyle: { color: 'rgba(255,255,255,0.75)' },
        data: ['实时负载率', '参考线'],
      },
      grid: { top: 56, right: 16, bottom: 16, left: 16 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: trend.map(item => item.time),
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: { color: 'rgba(255,255,255,0.65)', formatter: '{value}%' },
      },
      series: [
        {
          name: '实时负载率',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: trend.map(item => item.loadRate),
          lineStyle: { width: 2, color: '#16DD8E' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(22,221,142,0.58)' },
              { offset: 1, color: 'rgba(22,221,142,0)' },
            ]),
          },
        },
        {
          name: '参考线',
          type: 'line',
          symbol: 'none',
          data: trend.map(item => item.referenceRate),
          lineStyle: { width: 1, type: 'dashed', color: '#FFCB47' },
        },
      ],
    }),
    [trend]
  );

  return (
    <div className={styles.realtime}>
      <div className={styles.title}>
        <img className={styles.titleIcon} src={titleIcon} alt="" />
        <span>{mediumName}全厂今日实时负载率</span>
        <span>{mediumName}今日 + 全厂视图展示日内实时负载曲线</span>
      </div>
      <div className={styles.cards}>
        <div className={styles.card}>
          <span>当前负载率</span>
          <span>{formatPercent(current)}</span>
        </div>
        <div className={styles.card}>
          <span>今日最高</span>
          <span>{formatPercent(max)}</span>
        </div>
        <div className={styles.card}>
          <span>今日平均</span>
          <span>{formatPercent(avg)}</span>
        </div>
        <div className={styles.card}>
          <span>80%以上持续</span>
          <span>{getHighDuration(trend)}</span>
        </div>
      </div>
      <div className={styles.chart}>
        <EchartsWithTheme option={option} style={{ height: '100%', width: '100%' }} theme={theme} />
      </div>
    </div>
  );
};

export default RealtimeLoadSection;
